import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TimeAgo from 'react-timeago'
import { timespanToString } from "../../../utils/timeUtils";
import {
    LabelLarge,
    ParagraphSmall,
    ParagraphXSmall,
} from 'baseui/typography';
import { Block } from 'baseui/block';
import { AlarmDetailsDto, getBehavior, isAcknowledged, useAckAlarmByIdMutation, needsAcknowledge } from '../../../services/alarm'
import { useGetObjectByIdQuery } from '../../../services/objects'
import {
    NumberedStep,
    ProgressSteps,
    Step
} from "baseui/progress-steps";
import { Button, KIND, SIZE, SHAPE } from 'baseui/button';
import { ButtonGroup } from 'baseui/button-group';
import { AckUserInfo } from '../AckUserInfo'
import { TiFlowSwitch } from 'react-icons/ti'
import { IoLogoAppleAr } from 'react-icons/io5'
import { HierarchyObjectNavigation } from "../../../components/HierarchyNavigation";
import { GetObjectUrl } from "../../../utils/navigationService";
import { useIntl, FormattedMessage } from 'react-intl';
import { useTimeAgoFormatter } from "../../../hooks/useTimeAgoFormatter";
import { BsStopwatch } from "react-icons/bs";
import { getAlarmClassStyle } from "../alarmClassStyles";
import { useStyletron } from "baseui";
import { Tag } from "baseui/tag";
import { getBimId } from "../../../automationObjects/utils";
import { useIntlAlarmMessage } from "../../../hooks/useIntlAlarmMessage";

const AlarmDetailsView = (props: AlarmDetailsDto) => {
    const [, theme] = useStyletron();
    const intl = useIntl();
    const { formatter } = useTimeAgoFormatter();
    const alarm = props;
    const behavior = getBehavior(alarm);
    const nav = useNavigate();

    const style1 = getAlarmClassStyle(alarm.alarmClass.name, alarm.offTime !== undefined && alarm.offTime !== null, theme);

    const [current, setCurrent] = useState(0);

    const needsAck = needsAcknowledge(alarm);
    const isAcked = isAcknowledged(alarm);
    const { data: objectData } = useGetObjectByIdQuery({ resourceGroupId: alarm.object.resourceGroupId, id: alarm.objectId });

    const [ack, { data, isLoading, error }] = useAckAlarmByIdMutation()

    useEffect(() => {
        const cur = alarm.offTime ? 2 : alarm.ackTime ? 1 : 0;
        setCurrent(cur);
    }, [alarm]);

    const bimId = getBimId(objectData);

    const alarmMessage = useIntlAlarmMessage(alarm);
    return (
        <Block display='flex' flexDirection='column' justifyContent='flex-start' gridGap='30px'>
            <Block width='100%' display='flex' alignItems='center' padding={theme.sizing.scale200} flexDirection='row' gridGap='20px' overrides={{
                Block: {
                    style: {
                        backgroundColor: style1?.backgroundColor,
                        color: style1?.color,
                        borderRadius: theme.sizing.scale200
                    }
                }
            }}>
                <>{style1?.icon}</>
                <Header alarm={alarm} behavior={behavior} style={style1}></Header>

            </Block>

            <Block display='flex' alignItems='flex-start' flexDirection='column' gridGap='30px'>

                <Tag closeable={false} color={theme.colors.primary500} kind="custom" variant="outlined" size="small" overrides={{
                    Root: {
                        style: ({ $theme }) => ({
                            borderWidth: '1px',
                            marginLeft: '0px',
                        })
                    },
                    Text: {
                        style: ({ $theme }) => ({
                            fontSize: theme.typography.ParagraphXSmall.fontSize,
                        })
                    }
                }}>{intl.formatMessage({ id: `alarmClassNames.${alarm.alarmClass.name}`, defaultMessage: alarm.alarmClass.name })}</Tag>
                {(needsAck && isAcked) && <AckUserInfo userId={alarm.ackUserId} userName={alarm.ackUser || ''} time={alarm.ackTime}></AckUserInfo>}
                {(needsAck && !isAcked) && <ParagraphSmall>
                    <Button disabled={isAcked} isLoading={isLoading} size={SIZE.compact} onClick={() => {
                        ack({ alarmId: alarm.id, resourceGroupId: alarm.resourceGroupId });
                    }}><FormattedMessage id='alarms.ackAlarm' defaultMessage='Acknowledge' /></Button>
                </ParagraphSmall>}
                <LabelLarge><FormattedMessage id='alarms.Status' defaultMessage='State' /></LabelLarge>
                <ProgressSteps current={current}>
                    <NumberedStep title={intl.formatMessage({ id: `alarms.StatusOn`, defaultMessage: "Alarm ON" })} >
                        <ParagraphXSmall>
                            <TimeAgo date={alarm.onTime} formatter={formatter}></TimeAgo>
                        </ParagraphXSmall>
                        <ParagraphSmall>
                            {alarmMessage}
                        </ParagraphSmall>
                        <ButtonGroup shape={SHAPE.pill}
                            kind={KIND.secondary}
                            size={SIZE.mini}>
                            <Button disabled><FormattedMessage id='prev' defaultMessage='prev' /></Button>
                            <Button onClick={() => setCurrent(1)}><FormattedMessage id='next' defaultMessage='next' /></Button>
                        </ButtonGroup>
                    </NumberedStep>
                    <NumberedStep title={intl.formatMessage({ id: `alarms.StatusAcknowledged`, defaultMessage: "Alarm Acknowledged" })} >
                        {needsAck && isAcked && <AckUserInfo userId={alarm.ackUserId} userName={alarm.ackUser || ''} time={alarm.ackTime}></AckUserInfo>}
                        {!needsAck && <ParagraphSmall><FormattedMessage id='alarms.noNeedToAck' defaultMessage='this alarm has no need to be acknowledged' /></ParagraphSmall>}
                        <ButtonGroup shape={SHAPE.pill}
                            kind={KIND.secondary}
                            size={SIZE.mini}>
                            <Button onClick={() => setCurrent(0)}><FormattedMessage id='prev' defaultMessage='prev' /></Button>
                            <Button onClick={() => setCurrent(2)}><FormattedMessage id='next' defaultMessage='next' /></Button>
                        </ButtonGroup>
                    </NumberedStep>
                    <NumberedStep title={intl.formatMessage({ id: `alarms.StatusOff`, defaultMessage: "Alarm OFF" })} >
                        {alarm.offTime &&
                            (<>
                                <ParagraphXSmall>
                                    <TimeAgo date={alarm.offTime || new Date()} formatter={formatter}></TimeAgo>
                                </ParagraphXSmall>
                            </>)
                        }
                        {!alarm.offTime && <ParagraphSmall><FormattedMessage id='alarms.stillActive' defaultMessage='Alarm is still active' /></ParagraphSmall>}
                        <ButtonGroup shape={SHAPE.pill}
                            kind={KIND.secondary}
                            size={SIZE.mini}>
                            <Button onClick={() => setCurrent(1)}><FormattedMessage id='prev' defaultMessage='prev' /></Button>
                            <Button disabled><FormattedMessage id='next' defaultMessage='next' /></Button>
                        </ButtonGroup>
                    </NumberedStep>
                </ProgressSteps>
            </Block>
            <LabelLarge><FormattedMessage id='alarms.viewElementIn' defaultMessage='View App in' /></LabelLarge>
            <Block display='flex' alignItems='flex-start' flexDirection='row' justifyContent='flex-start' gridGap='30px'>
                <ParagraphSmall>
                    <Button shape={SHAPE.circle} kind={KIND.secondary} onClick={() => nav(`${GetObjectUrl(alarm.resourceGroupId, alarm.objectId)}?defaultView=process`)} >
                        <TiFlowSwitch />
                    </Button>
                    <Block display='block'>
                        <FormattedMessage id='process' defaultMessage='Process' />
                    </Block>
                </ParagraphSmall>
                {bimId &&

                    <ParagraphSmall>
                        <Button shape={SHAPE.circle} kind={KIND.secondary} onClick={() => nav(`/bim/${bimId}`)} >
                            <IoLogoAppleAr />
                        </Button>
                        <Block display='block'>
                            BIM
                        </Block>
                    </ParagraphSmall>

                }
            </Block>

            {alarm.duration && <div>
                <LabelLarge><FormattedMessage id='duration' defaultMessage='Duration' /></LabelLarge>
                <ParagraphXSmall >
                    <>
                        <BsStopwatch ></BsStopwatch>
                        <span> {timespanToString(Math.round(alarm.duration))}</span>
                    </>
                </ParagraphXSmall>
            </div>}
        </Block>
    )
}



function Header({ alarm, behavior, style }: any) {
    const intl = useIntl();

    const alarmMessage = useIntlAlarmMessage(alarm);
    return (
        <Block width='100%' display='flex' flexDirection='column' gridGap='10px'  >
            <LabelLarge color={style.color}>{alarmMessage}</LabelLarge>
            <HierarchyObjectNavigation resourceGroupId={alarm.object.resourceGroupId} objectId={alarm.objectId} currentDescription={alarm.object.description} current={alarm.object.name} showDescription={true} currentAsLink={true}></HierarchyObjectNavigation>
        </Block>)
}


export default AlarmDetailsView;